import {
  DataTable,
  DateText,
  DisciplineTag,
  StatusChip,
  isolate,
  type DataTableProps,
} from '@batiflow/ui';
import { Checkbox, __css, tokens } from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  ErrorCircle16Filled,
  ShieldTask16Regular,
} from '@fluentui/react-icons';
import { useMemo, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { InApprovalBadge } from './approvals/approval-badges';
import type { DocumentRow, DocumentSort, SortState } from './document-model';

const useStyles = __css({"title":{"Btd35i7":"fjksvth","Bhrd7zp":"fl43uef"},"code":{"Huce71":"fz5stix","Bahqtrf":"f1fedwem"},"muted":{"sj55zd":"f11d4kpn"},"srOnly":{"qhf8xq":"f1euv43f","Bns2gvi":"fittdk8","st59p4":"f156r0dy","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","Bgl5zvf":"f1wch0ki","Huce71":"fz5stix"},"status":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","Belr9w4":"f1j0q4x9"}});

type Columns = DataTableProps<DocumentRow>['columns'];
type SortingState = NonNullable<DataTableProps<DocumentRow>['sorting']>;

const SORTABLE: Record<string, DocumentSort> = {
  code: 'code',
  title: 'title',
  modified: 'updatedAt',
};
const COLUMN_OF: Record<DocumentSort, string> = {
  code: 'code',
  title: 'title',
  updatedAt: 'modified',
};

/** The table's sorting state for the list's (the server sorts). */
export const sortingOf = (state: SortState): SortingState => [
  { id: COLUMN_OF[state.sort], desc: state.order === 'desc' },
];

/** The list's sort for the table's sorting state; `null` when the sorting was removed. */
export function sortFromTable(sorting: SortingState): SortState | null {
  const first = sorting[0];
  const sort = first ? SORTABLE[first.id] : undefined;
  return first && sort ? { sort, order: first.desc ? 'desc' : 'asc' } : null;
}

const STATUS_ICON: Record<string, ReactElement> = {
  uploading: <ShieldTask16Regular />,
  scanning: <ShieldTask16Regular />,
  available: <CheckmarkCircle16Filled />,
  rejected: <ErrorCircle16Filled />,
};
const STATUS_INTENT = {
  uploading: 'informative',
  scanning: 'informative',
  available: 'success',
  rejected: 'danger',
} as const;

/** The state of the latest version as icon and text. */
function StatusCell({ document }: { document: DocumentRow }) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const status = document.latestVersionStatus;
  return (
    <div className={styles.status}>
      {status ? (
        <StatusChip
          label={t(`status.${status}`)}
          intent={STATUS_INTENT[status]}
          icon={STATUS_ICON[status] as ReactElement}
        />
      ) : (
        <span className={styles.muted}>{t('list.none')}</span>
      )}
    </div>
  );
}

export interface DocumentsTableProps {
  documents: readonly DocumentRow[];
  sort: SortState;
  onSortChange: (sort: SortState | null) => void;
  selected: ReadonlySet<string>;
  onToggle: (id: string, selected: boolean) => void;
  onToggleAll: (selected: boolean) => void;
  /** A row was activated (click, Enter or Space): the panel opens on it. */
  onOpen: (document: DocumentRow) => void;
  loading: boolean;
  empty: ReactNode;
}

/**
 * The files list: a checkbox, code, title, index, discipline, phase, status, modified date and
 * author organisation. Code, title and modified sort on the server. The approval column says
 * "In approval" while a flow holds the document (`lockedByFlowId`).
 */
export function DocumentsTable({
  documents,
  sort,
  onSortChange,
  selected,
  onToggle,
  onToggleAll,
  onOpen,
  loading,
  empty,
}: DocumentsTableProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();

  const allSelected = documents.length > 0 && documents.every((row) => selected.has(row.id));
  const someSelected = !allSelected && documents.some((row) => selected.has(row.id));

  const columns = useMemo<Columns>(
    () => [
      {
        id: 'select',
        enableSorting: false,
        meta: { minWidth: 44 },
        header: () => (
          <Checkbox
            aria-label={t('list.selectAll')}
            checked={allSelected ? true : someSelected ? 'mixed' : false}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onChange={(_event, data) => {
              onToggleAll(data.checked === true);
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={t('list.selectRow', { name: isolate(row.original.title) })}
            checked={selected.has(row.original.id)}
            // A click on the box selects the document; it does not open the panel.
            onClick={(event) => {
              event.stopPropagation();
            }}
            onChange={(_event, data) => {
              onToggle(row.original.id, data.checked === true);
            }}
          />
        ),
      },
      {
        id: 'code',
        // An accessor makes the column sortable (the server sorts: the value is not read).
        accessorFn: (row) => row.code ?? '',
        header: t('list.columns.code'),
        meta: { minWidth: 190 },
        cell: ({ row }) =>
          row.original.code ? (
            <bdi dir="ltr" className={styles.code}>
              {row.original.code}
            </bdi>
          ) : (
            <span className={styles.muted} title={t('list.codeMissing')}>
              <span aria-hidden="true">—</span>
              <span className={styles.srOnly}>{t('list.codeMissing')}</span>
            </span>
          ),
      },
      {
        id: 'title',
        accessorFn: (row) => row.title,
        header: t('list.columns.title'),
        meta: { minWidth: 220 },
        cell: ({ row }) => <bdi className={styles.title}>{row.original.title}</bdi>,
      },
      {
        id: 'index',
        header: t('list.columns.index'),
        enableSorting: false,
        meta: { minWidth: 80 },
        cell: ({ row }) =>
          row.original.revisionIndex ? (
            <bdi dir="ltr">{row.original.revisionIndex}</bdi>
          ) : (
            <span className={styles.muted}>{t('list.none')}</span>
          ),
      },
      {
        id: 'discipline',
        header: t('list.columns.discipline'),
        enableSorting: false,
        meta: { minWidth: 130 },
        cell: ({ row }) =>
          row.original.discipline ? (
            <DisciplineTag discipline={row.original.discipline} />
          ) : (
            <span className={styles.muted}>{t('list.none')}</span>
          ),
      },
      {
        id: 'phase',
        header: t('list.columns.phase'),
        enableSorting: false,
        meta: { minWidth: 100 },
        cell: ({ row }) =>
          row.original.phaseType ? (
            t(`projects:phaseTypes.${row.original.phaseType}`)
          ) : (
            <span className={styles.muted}>{t('list.none')}</span>
          ),
      },
      {
        id: 'status',
        header: t('list.columns.status'),
        enableSorting: false,
        meta: { minWidth: 170 },
        cell: ({ row }) => <StatusCell document={row.original} />,
      },
      {
        id: 'approval',
        header: t('list.columns.approval'),
        enableSorting: false,
        meta: { minWidth: 150 },
        cell: ({ row }) =>
          row.original.lockedByFlowId ? (
            <span title={t('status.lockedHint')}>
              <InApprovalBadge />
            </span>
          ) : (
            <span className={styles.muted}>{t('list.none')}</span>
          ),
      },
      {
        id: 'modified',
        accessorFn: (row) => row.updatedAt,
        header: t('list.columns.modified'),
        meta: { minWidth: 120 },
        cell: ({ row }) => <DateText value={row.original.updatedAt} />,
      },
      {
        id: 'author',
        header: t('list.columns.author'),
        enableSorting: false,
        meta: { minWidth: 160 },
        cell: ({ row }) =>
          row.original.ownerOrgName ? (
            <bdi>{row.original.ownerOrgName}</bdi>
          ) : (
            <span className={styles.muted}>{t('list.partner')}</span>
          ),
      },
    ],
    [t, styles, selected, allSelected, someSelected, onToggle, onToggleAll],
  );

  return (
    <DataTable<DocumentRow>
      columns={columns}
      data={documents}
      getRowId={(row) => row.id}
      manualSorting
      sorting={sortingOf(sort)}
      onSortingChange={(sorting) => {
        onSortChange(sortFromTable(sorting));
      }}
      loading={loading}
      empty={empty}
      caption={t('list.caption')}
      onRowClick={onOpen}
    />
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/documents-table.griffel.css";